import type { PublicOrgInfo } from 'mock/generated/types';
import { matchPath, useLocation } from 'react-router-dom';
import { number, object } from 'yup';

import {
  useGetProjectQuery,
  usePublicOrgInfoQuery,
} from 'src/shared/graphql/generated';
import { isProduction } from 'src/shared/helpers/environment';
import { useGlobalStorageContext } from 'src/shared/hooks/useGlobalStorage';
import { parseParams } from 'src/shared/hooks/useParsedParams';
import { Route } from 'src/shared/route';

import type { CustomizationCategory } from '../../constants/customizationCategories';
import {
  DESIGNER_PARTNER_MAP,
  DesignerPartner,
  LEAD_FORM_CONFIGURATION_MAP,
  PARTNER_CONFIGURATION_MAP,
} from '../../constants/partners';
import { customizeReferrerSchema } from '../../schemas';

export const isCobrandCategory = (
  brandType: string,
  category: CustomizationCategory | null,
) => {
  if (!category) return false;

  return category.type === brandType || category.region === brandType;
};

export const useGetPublicOrgInfoFromGlobalStorage = () => {
  const publicIdentifier = useGlobalStorageContext().affOrgPublicId;
  const { data: publicOrgInfo } = usePublicOrgInfoQuery({
    variables: { publicIdentifier: publicIdentifier || '' },
    skip: !publicIdentifier,
  });

  return publicOrgInfo?.publicOrgInfo;
};

export const useGetPublicOrgInfoFromPath = () => {
  const { state: locationState } = useLocation();

  const { referrer } = customizeReferrerSchema.isValidSync(locationState)
    ? locationState
    : { referrer: null };

  const projectParams = referrer
    ? matchPath(referrer, {
        path: Route.HOMEOWNER_APP_PROJECT.path,
      })?.params ?? {}
    : null;

  const projectId = projectParams
    ? parseParams(projectParams, object({ projectId: number() })).projectId
    : null;

  const { data } = useGetProjectQuery({
    variables: {
      projectId: Number(projectId),
    },
    skip: !projectId,
  });

  return data?.project?.publicOrgInfo;
};

export const useGetCobrandInfo = (
  publicOrgInfo?: PublicOrgInfo | null,
  leadFormConfigId?: number,
) => {
  const orgId = publicOrgInfo?.id?.toString();
  const partnerOrgs = isProduction()
    ? DESIGNER_PARTNER_MAP.production
    : DESIGNER_PARTNER_MAP.staging;

  const partnerOrgKey = orgId && partnerOrgs ? partnerOrgs[orgId] : null;

  const brandName = publicOrgInfo?.displayName ?? undefined;
  const brandLogo = publicOrgInfo?.brandLogoUrl ?? undefined;

  const { brandType, defaultLeadFormConfiguration } =
    PARTNER_CONFIGURATION_MAP[partnerOrgKey || DesignerPartner.NONE];

  const leadFormConfigIdForOrg =
    leadFormConfigId || defaultLeadFormConfiguration;

  const { cta, hasLeadForm, leadFormFields } =
    LEAD_FORM_CONFIGURATION_MAP[leadFormConfigIdForOrg];

  return { brandName, brandLogo, brandType, leadFormFields, cta, hasLeadForm };
};
